/**
 * Shared date/time helpers for visit dates and times.
 *
 * THE GOTCHA these exist to prevent: `new Date('2026-07-27')` parses a
 * date-only string as UTC midnight, which is the PREVIOUS EVENING in every
 * US timezone. Any visit date parsed that way displays one day early
 * (the Session 80 shared-summary bug). Likewise `toISOString()` converts
 * to UTC first, so stamping "today" with it rolls the date FORWARD for
 * evening recordings.
 *
 * Rules:
 * - Parsing a visit date string -> parseLocalDate / formatVisitDate
 * - Stamping "today" as YYYY-MM-DD  -> formatLocalYMD (never toISOString)
 * - Displaying a visit time         -> formatVisitTime (DB returns "15:01:00")
 * - Full ISO timestamps (message createdAt etc.) are safe to pass to
 *   `new Date()` directly; these helpers are for DATE-only and TIME-only values.
 */

/**
 * Parse a visit date as a LOCAL date, avoiding the UTC-midnight shift.
 * Accepts: a Date object, "YYYY-MM-DD", or an ISO timestamp like
 * "2026-07-27T00:00:00.000Z" (Postgres `date` columns serialize this way).
 * Returns a Date at local midnight, or null if unparseable.
 */
export const parseLocalDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) {
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  if (typeof raw !== 'string') return null;
  // Strip time portion off ISO timestamps: the date part is the stored value.
  const ymd = raw.includes('T') ? raw.split('T')[0] : raw;
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  const [, y, m, d] = match.map(Number);
  return new Date(y, m - 1, d);
};

/** Format today's (or the given) date as local "YYYY-MM-DD". */
export const formatLocalYMD = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Format a visit date for display, e.g. "Monday, July 27, 2026".
 * Pass toLocaleDateString options to change the style.
 */
export const formatVisitDate = (raw, options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) => {
  const date = parseLocalDate(raw);
  if (!date) return '';
  return date.toLocaleDateString('en-US', options);
};

/**
 * Format a visit time for display as "3:01 PM".
 * Accepts Postgres `time` values ("15:01:00"), "HH:MM", or an
 * already-formatted "3:01 PM" (returned unchanged).
 */
export const formatVisitTime = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return raw;
  let hours = Number(match[1]);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${period}`;
};
